import React from "react"
import { Col, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap"

import CustomButton from "@/_components/CustomButton"
import FormInput from "@/_components/FormInput"
import Table from "@/_components/Table"

import { getLocale, loc } from "@/_services/localization"
import { addOops } from "@/_services/notification"
import { getUsername } from "@/_services/userConfiguration"
import { formatDateTime } from "@/_services/utils"
import axios from "axios"
import { getEntityServerRoute, parseBoolean, toQueryString } from "basikon-common-utils"

class HistoryModal extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      fieldAudit: {},
    }
  }

  componentDidMount() {
    const { withAudit } = this.props
    if (withAudit) this.getFieldAudit(withAudit)
  }

  getFieldAudit = async withAudit => {
    const statePatch = { loading: false }
    try {
      this.setState({ loading: true })
      const { entityName, entityRegistration, field } = this.props
      let fieldAudit = {}
      if (typeof withAudit === "function") {
        fieldAudit = await withAudit({ entityName, entityRegistration, field })
      } else if (Array.isArray(withAudit)) {
        // Be careful, an array is also an object!
        fieldAudit.data = [...withAudit]
      } else if (typeof withAudit === "object") {
        if ("data" in withAudit) {
          fieldAudit = { ...withAudit }
        } else {
          const _entityName = withAudit.entityName || entityName
          const _entityRegistration = withAudit.entityRegistration || entityRegistration
          let data = []
          if (_entityName && _entityRegistration) {
            const serverRoute = getEntityServerRoute(_entityName)
            const auditQuery = { field, who: parseBoolean(withAudit.myAuditOnly) ? getUsername() : undefined }
            const fieldAudits = (await axios.get(`${serverRoute}/${_entityRegistration}/audits-fields${toQueryString(auditQuery)}`)).data
            data = [...fieldAudits]
          }
          fieldAudit = { ...withAudit, data }
        }
      } else if (typeof withAudit === "string" && isPath(withAudit)) {
        const url = withAudit.startsWith("/api/") ? withAudit : `/api/script/runs/${withAudit}`
        fieldAudit = (await axios.get(url)).data
      } else if (entityName && entityRegistration) {
        const serverRoute = getEntityServerRoute(entityName)
        const auditQuery = { field }
        const fieldAudits = (await axios.get(`${serverRoute}/${entityRegistration}/audits-fields${toQueryString(auditQuery)}`)).data
        fieldAudit.data = [...fieldAudits]
      }
      fieldAudit.title ||= parseBoolean(fieldAudit.myAuditOnly) ? "My audit" : "Audit"
      statePatch.fieldAudit = fieldAudit
    } catch (error) {
      addOops(error)
    }
    this.setState(statePatch)
  }

  render() {
    const { loading, fieldAudit } = this.state
    const { title: propsTitle, columns: propsColumns, data: propsData, obj, field, type: propsType, select: propsSelect, onClose } = this.props

    const title = fieldAudit.title || propsTitle || "History"
    const columns = fieldAudit.columns || propsColumns || []
    let data = fieldAudit.data || propsData || obj?.[field] || []
    const type = fieldAudit.type || propsType
    const select = fieldAudit.select || propsSelect
    const locale = getLocale()

    if (columns.length === 0) {
      if (parseBoolean(fieldAudit.myAuditOnly)) {
        columns.push({ title: "Date", name: "date" }, { title: loc("Old value"), name: "prevValue" }, { title: loc("New value"), name: "value" })
      } else {
        columns.push({ title: "Date", name: "date" }, { title: "User", name: "username" }, { title: "Value", name: "value" })
      }
    }
    const columnDateIsDate = columns.find(c => c.name === "date")?.type === "date"

    data = data.map(it => {
      return {
        ...it,
        date: columnDateIsDate ? it.date : formatDateTime(it.date, locale),
        prevValue:
          "prevValue" in it ? (
            <OverlayTrigger placement="top" overlay={<Tooltip id="grossPrevValue">{`"${it.prevValue?.toString()}"`}</Tooltip>}>
              <div className="d-inline-block">
                <FormInput
                  inArray
                  value={getValue(type, it.prevValue)}
                  type={getType(type, it.prevValue)}
                  select={getSelect(select, it.prevValue)}
                  readOnly
                />
              </div>
            </OverlayTrigger>
          ) : undefined,
        value:
          "value" in it ? (
            <OverlayTrigger placement="top" overlay={<Tooltip id="grossValue">{`"${it.value?.toString()}"`}</Tooltip>}>
              <div className="d-inline-block">
                <FormInput inArray value={getValue(type, it.value)} type={getType(type, it.value)} select={getSelect(select, it.value)} readOnly />
              </div>
            </OverlayTrigger>
          ) : undefined,
      }
    })

    return (
      <Modal show={true} onHide={onClose} backdrop="static" className="history-modal">
        <Modal.Header closeButton>
          <Modal.Title>{loc(title)}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row>
            <Col xs={12}>
              <Table columns={columns} data={data} loading={loading} />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <CustomButton bsSize="sm" onClick={onClose}>{loc`Close`}</CustomButton>
        </Modal.Footer>
      </Modal>
    )
  }
}

export default HistoryModal

function isPath(pathname) {
  if (!pathname) return false
  if (pathname === "/") return false
  if (pathname.startsWith("/") && pathname.includes("=") && !pathname.includes("?")) return false // /code=XX&state=YY
  return true
}

function getValue(type, value) {
  const parsedType = getType(type, value)
  return parsedType === "text" ? value?.toString() : value
}

function getType(type, value) {
  const parsedType = (type || "text").toLowerCase()
  if (["text", "checkbox"].includes(parsedType)) return "text"
  return value === "null" || value === "undefined" ? "text" : type
}

function getSelect(select, value) {
  if (!select) return
  return value === "null" || value === "undefined" ? undefined : select
}
